// src/Accordion/Accordion.tsx
import {
  Accordion as MuiAccordion,
  accordionClasses,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  styled as styled2,
  Typography as Typography2
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import React3, { useCallback, useState } from "react";

// src/utils/AnimationWrapper.tsx
import React from "react";
import { AttentionSeeker, Bounce, Fade, JackInTheBox, Zoom } from "react-awesome-reveal";
function AnimationWrapper({ animation, children, ...rest }) {
  let Component;
  switch (animation) {
    case "JackInTheBox":
      Component = JackInTheBox;
      break;
    case "AttentionSeeker":
      Component = AttentionSeeker;
      break;
    case "Bounce":
      Component = Bounce;
      break;
    case "Fade":
      Component = Fade;
      break;
    case "Zoom":
      Component = Zoom;
      break;
    default:
      Component = void 0;
  }
  return Component ? (
    // Set triggerOnce to prevent glitch as described in here:
    // https://github.com/morellodev/react-awesome-reveal/issues/168, needs to be checked with SSR
    /* @__PURE__ */ React.createElement(Component, { cascade: true, delay: 200, fraction: 0.3, duration: 500, triggerOnce: true, ...rest }, children)
  ) : children;
}
var AnimationWrapper_default = AnimationWrapper;

// src/utils/Subtitle.tsx
import { styled, Typography } from "@mui/material";
import React2 from "react";
var baseClass = "Subtitle";
var classes = {
  title: `${baseClass}-title`,
  description: `${baseClass}-description`
};
var subtitleClasses = classes;
var Root = styled("div")(({ theme }) => ({
  [`& .${classes.title}`]: {
    textAlign: "center",
    marginBottom: theme.spacing(1)
  },
  [`& .${classes.description}`]: {
    ...theme.typography.body1,
    textAlign: "center",
    margin: "0 auto",
    [theme.breakpoints.up("md")]: {
      maxWidth: "calc(8/12*100%)"
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "calc(6/12*100%)"
    }
  }
}));
function Subtitle({ title, description, ...rest }) {
  if (!title && !description) return null;
  return /* @__PURE__ */ React2.createElement(Root, { ...rest }, !!title && /* @__PURE__ */ React2.createElement(Typography, { variant: "h4", className: classes.title }, title), !!description && /* @__PURE__ */ React2.createElement("div", { className: classes.description }, description));
}
var Subtitle_default = Subtitle;

// src/Accordion/Accordion.tsx
var CustomAccordion = styled2(MuiAccordion)(() => ({
  boxShadow: "none",
  backgroundColor: "transparent",
  [`&.${accordionClasses.expanded}`]: {
    margin: 0,
    "&:before": {
      opacity: 1
    }
  },
  [`&.${accordionClasses.root}`]: {
    "&:before": {
      height: 0
    },
    "&+&:before": {
      height: 1
    }
  }
}));
var CustomAccordionSummary = styled2(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  "& h5": {
    fontWeight: theme.typography.fontWeightMedium
  },
  [`& .${accordionSummaryClasses.content}`]: {
    margin: theme.spacing(1, 0),
    [`&.${accordionSummaryClasses.expanded}`]: {
      margin: theme.spacing(1, 0)
    }
  }
}));
var CustomAccordionDetails = styled2(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 2)
}));
function Accordion({ title, description, items, animation, ...rest }) {
  const [expanded, setExpanded] = useState("");
  const handleChange = useCallback((panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : "");
  }, []);
  return /* @__PURE__ */ React3.createElement(Box, { ...rest }, /* @__PURE__ */ React3.createElement(Subtitle_default, { title, description, sx: { mb: 2 } }), items?.map((item, index) => {
    const panelId = `panel-${index}`;
    return /* @__PURE__ */ React3.createElement(
      CustomAccordion,
      {
        key: `accordion-${index}`,
        expanded: expanded === panelId,
        onChange: handleChange(panelId)
      },
      /* @__PURE__ */ React3.createElement(
        CustomAccordionSummary,
        {
          expandIcon: /* @__PURE__ */ React3.createElement(ExpandMore, null),
          "aria-controls": `accordion-details-${index}`,
          id: `panel-${index}-header`
        },
        /* @__PURE__ */ React3.createElement(AnimationWrapper_default, { animation, delay: 200 * index + 200, triggerOnce: true }, /* @__PURE__ */ React3.createElement(Typography2, { variant: "body1", component: "h5" }, item.title))
      ),
      /* @__PURE__ */ React3.createElement(
        CustomAccordionDetails,
        {
          id: `accordion-details-${index}`
        },
        item.description
      )
    );
  }));
}
var Accordion_default = Accordion;

// src/Carousel/Carousel.tsx
import { alpha as alpha2, Box as Box3, IconButton, styled as styled4, Tab, tabClasses, Tabs as MuiTabs, tabsClasses } from "@mui/material";
import React5, { useRef, useState as useState2 } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

// src/TextImage/TextImage.tsx
import { alpha, Box as Box2, Button, lighten, styled as styled3, Typography as Typography3 } from "@mui/material";
import React4 from "react";
import clsx from "clsx";

// src/TextImage/textImageClasses.ts
var baseClass2 = "TextImage";
var classes2 = {
  content: `${baseClass2}-content`,
  spacing: `${baseClass2}-spacing`,
  titleDescriptionButton: `${baseClass2}-titleDescriptionButton`,
  image: `${baseClass2}-image`,
  imageLeft: `${baseClass2}-image-left`,
  imageRight: `${baseClass2}-image-right`,
  imageTop: `${baseClass2}-image-top`,
  imageBottom: `${baseClass2}-image-bottom`,
  imageBackdrop: `${baseClass2}-image-backdrop`,
  rounded: `${baseClass2}-rounded`,
  clickableVariant: `${baseClass2}-clickable-variant`
};
var textImageClasses = classes2;

// src/TextImage/TextImage.tsx
var RootDiv = styled3("div")(({ theme }) => ({
  [`& .${textImageClasses.titleDescriptionButton}`]: {
    flexGrow: 1,
    "& > *": {
      margin: theme.spacing(1, 0)
    },
    "& > h3": {
      marginBottom: 0
    }
  },
  [`& .${textImageClasses.image}`]: {
    display: "flex",
    alignItems: "center",
    minHeight: "100%",
    lineHeight: 0,
    "& img": {
      maxWidth: "100%"
    }
  },
  [`& .${textImageClasses.imageBackdrop}`]: {
    padding: theme.spacing(3),
    background: `linear-gradient(180deg, ${theme.palette.background.contrast} 0%, ${lighten(theme.palette.background.contrast, 0.6)} 100%);`,
    flex: 1,
    justifyContent: "center",
    display: "flex"
  },
  [`& .${textImageClasses.rounded}`]: {
    borderRadius: theme.shape.borderRadius,
    "& > img": {
      borderRadius: theme.shape.borderRadius
    }
  },
  [`& .${textImageClasses.clickableVariant}`]: {
    border: `1px solid ${alpha(theme.palette.grey[600], 0.24)}`,
    color: theme.palette.text.primary,
    [`& .${textImageClasses.titleDescriptionButton}`]: {
      padding: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        padding: theme.spacing(4)
      }
    },
    gap: 0,
    borderRadius: theme.shape.borderRadius
  }
}));
function TextImage({
  title,
  description,
  imgSrc,
  imgProps,
  imgAlt,
  imgSide,
  buttonLabel,
  buttonLink,
  animation,
  variant,
  applyBorderRadius,
  alignImage,
  justifyContent,
  onload,
  children,
  ButtonLinkComponent,
  ...rest
}) {
  const flexDirection = {
    top: "column-reverse",
    bottom: "column",
    left: "row-reverse",
    right: "row"
  }[imgSide];
  const LinkComponent = ButtonLinkComponent || "a";
  return /* @__PURE__ */ React4.createElement(RootDiv, { ...rest }, /* @__PURE__ */ React4.createElement(
    Box2,
    {
      display: "flex",
      gap: {
        xs: 3,
        md: 5
      },
      flexDirection: {
        md: flexDirection,
        xs: imgSide === "top" ? "column-reverse" : "column"
      },
      className: clsx({
        [textImageClasses.clickableVariant]: variant === "clickable"
      }),
      component: variant === "clickable" && LinkComponent || "div",
      ...variant === "clickable" && buttonLink || {}
    },
    /* @__PURE__ */ React4.createElement(
      Box2,
      {
        className: clsx(textImageClasses.content, {
          [textImageClasses.imageLeft]: imgSide === "left",
          [textImageClasses.imageRight]: imgSide === "right",
          [textImageClasses.imageBottom]: imgSide === "bottom",
          [textImageClasses.imageTop]: imgSide === "top"
        }),
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "flex-start" }
      },
      /* @__PURE__ */ React4.createElement(
        Box2,
        {
          display: "flex",
          alignItems: "left",
          flexDirection: "column",
          justifyContent: justifyContent || "flex-start",
          className: textImageClasses.titleDescriptionButton
        },
        !!title && /* @__PURE__ */ React4.createElement(Typography3, { variant: "h3" }, title),
        !!description && /* @__PURE__ */ React4.createElement(Typography3, { variant: "body1", component: "div" }, description),
        buttonLabel && /* @__PURE__ */ React4.createElement("div", null, /* @__PURE__ */ React4.createElement(
          Button,
          {
            color: "primary",
            variant: "contained",
            component: variant !== "clickable" && LinkComponent || "span",
            ...buttonLink || {}
          },
          buttonLabel
        ))
      ),
      children && /* @__PURE__ */ React4.createElement("div", null, children)
    ),
    /* @__PURE__ */ React4.createElement(Box2, { flex: 1 }, /* @__PURE__ */ React4.createElement(AnimationWrapper_default, { animation }, /* @__PURE__ */ React4.createElement(Box2, { className: textImageClasses.image, sx: { justifyContent: alignImage || "center" } }, /* @__PURE__ */ React4.createElement(
      Box2,
      {
        className: clsx({
          [textImageClasses.imageBackdrop]: variant === "backdrop",
          [textImageClasses.rounded]: applyBorderRadius
        })
      },
      /* @__PURE__ */ React4.createElement(
        "img",
        {
          src: imgSrc,
          alt: imgAlt,
          ...imgProps,
          onLoad: onload
        }
      )
    ))))
  ));
}
var TextImage_default = TextImage;

// src/Carousel/carouselClasses.ts
var baseClass3 = "Carousel";
var classes3 = {
  content: `${baseClass3}-content`,
  description: `${baseClass3}-description`,
  tab: `${baseClass3}-tab`,
  tabs: `${baseClass3}-tabs`,
  panelContainer: `${baseClass3}-panelContainer`,
  controlBox: `${baseClass3}-controlBox`,
  tabCounter: `${baseClass3}-tabCounter`,
  iconButton: `${baseClass3}-iconButton`
};
var carouselClasses = classes3;

// src/Carousel/Carousel.tsx
var CarouselVariant = /* @__PURE__ */ ((CarouselVariant2) => {
  CarouselVariant2["Tabs"] = "tabs";
  CarouselVariant2["Buttons"] = "buttons";
  return CarouselVariant2;
})(CarouselVariant || {});
var RootDiv2 = styled4("div")(({ theme }) => ({
  // Next two are required for centered + scrollable Tabs (https://github.com/mui/material-ui/issues/10235#issuecomment-531505399)
  [`& .${tabsClasses.root}`]: {
    justifyContent: "center"
  },
  [`& .${tabsClasses.scroller}`]: {
    flexGrow: 0
  },
  [`& .${tabsClasses.indicator}`]: {
    backgroundColor: theme.palette.primary.main
  },
  [`& .${carouselClasses.tab}`]: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    fontSize: "1rem",
    minWidth: 0,
    padding: 0,
    margin: theme.spacing(0, 1),
    fontWeight: theme.typography.fontWeightRegular,
    [`&.${tabClasses.selected}`]: {
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  [`& .${carouselClasses.tabs}`]: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(3)
    }
  },
  [`& .${carouselClasses.panelContainer}`]: {
    overflow: "hidden",
    transition: "height 0.1s ease-in"
  },
  [`& .${carouselClasses.controlBox}`]: {
    display: "flex",
    direction: "row",
    marginLeft: theme.spacing(-1),
    [theme.breakpoints.down("md")]: {
      borderRadius: theme.shape.borderRadius
    }
  },
  [`& .${carouselClasses.iconButton}`]: {
    backgroundColor: theme.palette.common.white,
    boxShadow: `0 0 8px 0 ${alpha2(theme.palette.common.black, 0.2)}`,
    padding: theme.spacing(0.4),
    color: theme.palette.primary.light,
    margin: theme.spacing(1),
    "&:disabled": {
      boxShadow: "none"
    }
  },
  [`& .${carouselClasses.tabCounter}`]: {
    color: theme.palette.primary.main,
    margin: theme.spacing(1),
    display: "flex",
    alignItems: "center"
  },
  [`& .${textImageClasses.content}`]: {
    padding: theme.spacing(3, 2.5, 0, 2.5),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
      [`&.${textImageClasses.imageLeft}`]: {
        paddingLeft: theme.spacing(0)
      },
      [`&.${textImageClasses.imageRight}`]: {
        paddingRight: theme.spacing(0)
      }
    }
  }
}));
function Carousel({
  title,
  description,
  animation,
  variant,
  items,
  ...rest
}) {
  const tabPanelRef = useRef(null);
  const [currentTab, setCurrentTab] = useState2(0);
  const [tabPanelHeight, setTabPanelHeight] = useState2();
  const handleChange = (event, tabIndex) => {
    if (tabPanelHeight === void 0 && tabPanelRef.current) {
      setTabPanelHeight(tabPanelRef.current.getBoundingClientRect().height);
    }
    setCurrentTab(tabIndex);
  };
  const nextItem = () => {
    setCurrentTab((index) => Math.min(items.length - 1, index + 1));
  };
  const previousItem = () => {
    setCurrentTab((index) => Math.max(0, index - 1));
  };
  const handleImageReady = () => {
    if (tabPanelRef.current) {
      setTabPanelHeight(tabPanelRef.current.getBoundingClientRect().height);
    }
  };
  return /* @__PURE__ */ React5.createElement(RootDiv2, { ...rest }, /* @__PURE__ */ React5.createElement(Subtitle_default, { title, description, sx: { mb: 3 } }), variant === "tabs" /* Tabs */ && /* @__PURE__ */ React5.createElement(
    MuiTabs,
    {
      value: currentTab,
      onChange: handleChange,
      "aria-label": "Tabs",
      variant: "scrollable",
      allowScrollButtonsMobile: true,
      className: carouselClasses.tabs
    },
    items.map((tab, index) => /* @__PURE__ */ React5.createElement(
      Tab,
      {
        disableRipple: true,
        label: tab.tabTitle,
        key: `tab-${index}`,
        "aria-controls": `tab-panel-${index}`,
        className: carouselClasses.tab
      }
    ))
  ), /* @__PURE__ */ React5.createElement(Box3, { className: carouselClasses.panelContainer, sx: { height: tabPanelHeight } }, /* @__PURE__ */ React5.createElement("div", { ref: tabPanelRef }, /* @__PURE__ */ React5.createElement(AnimationWrapper_default, { animation, triggerOnce: true }, /* @__PURE__ */ React5.createElement(
    "div",
    {
      role: "tabpanel",
      id: `tab-panel-${currentTab}`,
      key: `tab-panel-${currentTab}`,
      "aria-labelledby": `tab-${currentTab}`
    },
    items.map((item, index) => /* @__PURE__ */ React5.createElement(
      TextImage_default,
      {
        key: `tab-panel-${index}`,
        hidden: currentTab !== index,
        title: item.title,
        description: item.description,
        imgSrc: item.imgSrc,
        imgProps: item.imgProps,
        imgAlt: item.imgAlt,
        imgSide: variant === "buttons" /* Buttons */ ? "left" : item.imgSide,
        variant: "default",
        applyBorderRadius: true,
        alignImage: "center",
        justifyContent: "center",
        onload: handleImageReady,
        sx: {
          ...variant === "buttons" /* Buttons */ && { backgroundColor: "grey.50" },
          borderRadius: 1
        }
      },
      variant === "buttons" /* Buttons */ && /* @__PURE__ */ React5.createElement("div", { className: carouselClasses.controlBox }, /* @__PURE__ */ React5.createElement(
        IconButton,
        {
          className: carouselClasses.iconButton,
          onClick: previousItem,
          size: "small",
          disabled: currentTab === 0,
          disableRipple: true
        },
        /* @__PURE__ */ React5.createElement(ArrowBack, null)
      ), /* @__PURE__ */ React5.createElement(
        IconButton,
        {
          className: carouselClasses.iconButton,
          onClick: nextItem,
          size: "small",
          disabled: currentTab === items.length - 1,
          disableRipple: true
        },
        /* @__PURE__ */ React5.createElement(ArrowForward, null)
      ), /* @__PURE__ */ React5.createElement("p", { className: carouselClasses.tabCounter }, `${currentTab + 1}/${items.length}`))
    ))
  )))));
}

// src/CircleBackgroundBanners/CircleBackgroundBanners.tsx
import React8 from "react";
import { Grid, gridClasses, styled as styled6 } from "@mui/material";

// src/CircleBackgroundBanners/CircleBackgroundBanner.tsx
import React7 from "react";
import { Box as Box4, styled as styled5, svgIconClasses, Typography as Typography4 } from "@mui/material";

// src/icons/Arrow.tsx
import React6 from "react";
function Arrow() {
  return /* @__PURE__ */ React6.createElement("svg", { viewBox: "0 0 10 8", version: "1.1", xmlns: "http://www.w3.org/2000/svg", "aria-labelledby": "arrowRight" }, /* @__PURE__ */ React6.createElement("g", { id: "arrowRight", fill: "none", stroke: "currentColor", transform: "translate(1, 0.5)" }, /* @__PURE__ */ React6.createElement("polyline", { id: "Path", points: "4.66666667 7 8 3.5 4.66666667 0" }), /* @__PURE__ */ React6.createElement("line", { x1: "8", y1: "3.5", x2: "0", y2: "3.5", id: "Path" })));
}
var Arrow_default = Arrow;

// src/CircleBackgroundBanners/circleBackgroundBannersClasses.ts
var baseClass4 = "CircleBackgroundBanners";
var classes4 = {
  root: `${baseClass4}-root`,
  banner: `${baseClass4}-banner`,
  bannerBackgroundBox: `${baseClass4}-bannerBackgroundBox`,
  bannerBackground: `${baseClass4}-bannerBackground`,
  bannerImage: `${baseClass4}-bannerImage`,
  bannerWrapper: `${baseClass4}-bannerWrapper`,
  bannerContent: `${baseClass4}-bannerContent`,
  bannerTitle: `${baseClass4}-bannerTitle`,
  bannerDescription: `${baseClass4}-bannerDescription`,
  bannerLinkIcon: `${baseClass4}-bannerLinkIcon`
};
var circleBackgroundBannersClasses = classes4;

// src/CircleBackgroundBanners/CircleBackgroundBanner.tsx
function BannerComponent({ Component, ...rest }) {
  const FinalComponent = Component || "a";
  return /* @__PURE__ */ React7.createElement(FinalComponent, { ...rest });
}
var Banner = styled5(BannerComponent, {
  shouldForwardProp: (prop) => prop !== "color"
})(({
  color,
  theme
}) => ({
  [`&.${circleBackgroundBannersClasses.banner}`]: {
    display: "block",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.boxShadows.subtle,
    backgroundColor: theme.palette.background.paper,
    transition: "0.2s ease-out",
    flex: 1,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.boxShadows.highlight,
      [`& .${svgIconClasses.root}`]: {
        color: theme.palette.primary.main
      }
    }
  },
  [`& .${circleBackgroundBannersClasses.bannerBackgroundBox}`]: {
    position: "absolute",
    overflow: "hidden",
    width: 80,
    minHeight: 90,
    zIndex: 0,
    borderTopLeftRadius: theme.shape.borderRadius
  },
  [`& .${circleBackgroundBannersClasses.bannerBackground}`]: {
    position: "absolute",
    top: -50,
    left: -60,
    width: 140,
    height: 140,
    borderRadius: "50%",
    backgroundColor: color ?? "none"
  },
  [`& .${circleBackgroundBannersClasses.bannerWrapper}`]: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    height: "100%",
    width: "100%",
    padding: theme.spacing(2),
    zIndex: theme.zIndex.modal,
    color: theme.palette.text.primary,
    "&:hover, &:focus": {
      textDecoration: "none",
      color: theme.palette.text.primary
    }
  },
  [`& .${circleBackgroundBannersClasses.bannerContent}`]: {
    textAlign: "left",
    height: "100%",
    flex: 1,
    minWidth: "50px",
    overflowWrap: "anywhere",
    ml: 0.5
  },
  [`& .${circleBackgroundBannersClasses.bannerImage}`]: {
    width: 40,
    height: 40,
    margin: theme.spacing(0, 3, 2, 0)
  },
  [`& .${circleBackgroundBannersClasses.bannerLinkIcon}`]: {
    color: theme.palette.grey[800],
    justifySelf: "flex-end",
    width: "1.25rem",
    height: "1.25rem",
    strokeWidth: 0.5
  }
}));
function CircleBackgroundBanner({
  banner: {
    title,
    description,
    link,
    icon,
    color
  },
  Component
}) {
  return /* @__PURE__ */ React7.createElement(
    Banner,
    {
      className: circleBackgroundBannersClasses.banner,
      color,
      Component,
      ...link || {}
    },
    /* @__PURE__ */ React7.createElement("div", { className: circleBackgroundBannersClasses.bannerBackgroundBox }, /* @__PURE__ */ React7.createElement("div", { className: circleBackgroundBannersClasses.bannerBackground })),
    /* @__PURE__ */ React7.createElement("div", { className: circleBackgroundBannersClasses.bannerWrapper }, /* @__PURE__ */ React7.createElement(Box4, { alignSelf: "flex-start", justifySelf: "flex-start" }, /* @__PURE__ */ React7.createElement("img", { src: icon, alt: title, className: circleBackgroundBannersClasses.bannerImage })), /* @__PURE__ */ React7.createElement("div", { className: circleBackgroundBannersClasses.bannerContent }, !!title && /* @__PURE__ */ React7.createElement(
      Typography4,
      {
        gutterBottom: true,
        variant: "h6",
        fontSize: {
          xs: "0.9375rem",
          sm: "1rem"
        },
        className: circleBackgroundBannersClasses.bannerTitle
      },
      title
    ), !!description && /* @__PURE__ */ React7.createElement(
      Typography4,
      {
        color: "grey.800",
        fontSize: {
          xs: "0.9375rem",
          sm: "1rem"
        },
        className: circleBackgroundBannersClasses.bannerDescription
      },
      description
    )), /* @__PURE__ */ React7.createElement("div", { className: circleBackgroundBannersClasses.bannerLinkIcon }, /* @__PURE__ */ React7.createElement(Arrow_default, null)))
  );
}

// src/CircleBackgroundBanners/CircleBackgroundBanners.tsx
var StyledGrid = styled6(Grid, {
  shouldForwardProp: (prop) => prop !== "jumpItems"
})(({ jumpItems, theme }) => ({
  ...jumpItems && {
    [theme.breakpoints.up("md")]: {
      paddingBottom: 62,
      [`& .${gridClasses.item}:first-of-type`]: {
        transform: "translateY(26px)"
      },
      [`& .${gridClasses.item}:nth-of-type(2)`]: {
        transform: "translateY(62px)"
      }
    }
  }
}));
function CircleBackgroundBanners({ items, jumpItems, columnCount, LinkComponent, ...rest }) {
  return /* @__PURE__ */ React8.createElement(
    StyledGrid,
    {
      container: true,
      spacing: 1,
      jumpItems,
      className: circleBackgroundBannersClasses.root,
      ...rest
    },
    !!items && items.map((banner, i) => /* @__PURE__ */ React8.createElement(Grid, { item: true, xs: 12, md: columnCount ? 12 / columnCount : true, key: i, sx: { display: "flex" } }, /* @__PURE__ */ React8.createElement(CircleBackgroundBanner, { Component: LinkComponent, banner })))
  );
}

// src/ContactBlock/ContactBlock.tsx
import { Box as Box5, Button as Button2, Grid as Grid2, styled as styled7, svgIconClasses as svgIconClasses2, Typography as Typography5 } from "@mui/material";
import { Call } from "@mui/icons-material";
import React9 from "react";

// src/ContactBlock/contactBlockClasses.ts
var baseClass5 = "ContactBlock";
var classes5 = {
  image: `${baseClass5}-image`,
  title: `${baseClass5}-title`,
  description: `${baseClass5}-description`,
  button: `${baseClass5}-button`
};
var contactBlockClasses = classes5;

// src/ContactBlock/ContactBlock.tsx
var RootDiv3 = styled7("div")(({ theme }) => ({
  background: theme.gradients.orange,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  [`& .${contactBlockClasses.image}`]: {
    width: "100%",
    maxWidth: 800
  },
  [`& .${contactBlockClasses.title}`]: {
    fontSize: "1.25rem",
    lineHeight: "1.9rem",
    fontWeight: theme.typography.fontWeightBold
  },
  [`& .${contactBlockClasses.description}`]: {
    margin: theme.spacing(0.5, 0, 1),
    color: theme.palette.grey[800]
  },
  [`& .${contactBlockClasses.button}`]: {
    textTransform: "none",
    margin: 0,
    fontSize: "0.8rem",
    lineHeight: "1.6rem",
    marginLeft: theme.spacing(-1.5),
    [`& .${svgIconClasses2.root}`]: {
      fontSize: "0.8rem"
    }
  }
}));
function ContactBlock({
  title,
  description,
  buttonLabel,
  buttonLink,
  imgSrc,
  imgAlt,
  phoneNumber,
  phoneText,
  LinkComponent,
  ...rest
}) {
  return /* @__PURE__ */ React9.createElement(RootDiv3, { ...rest }, /* @__PURE__ */ React9.createElement(Grid2, { container: true, spacing: 0, alignItems: "center" }, /* @__PURE__ */ React9.createElement(Grid2, { item: true, xs: 12, sm: 6 }, !!imgSrc && /* @__PURE__ */ React9.createElement(
    Box5,
    {
      display: "flex",
      justifyContent: "center",
      sx: (theme) => ({
        padding: {
          xs: theme.spacing(2),
          sm: theme.spacing(4, 8)
        }
      })
    },
    /* @__PURE__ */ React9.createElement("img", { className: contactBlockClasses.image, src: imgSrc, alt: imgAlt })
  )), /* @__PURE__ */ React9.createElement(
    Grid2,
    {
      item: true,
      xs: 12,
      sm: 6
    },
    /* @__PURE__ */ React9.createElement(
      Box5,
      {
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "center", sm: "flex-start" },
        p: 2
      },
      !!title && /* @__PURE__ */ React9.createElement(Box5, null, /* @__PURE__ */ React9.createElement(Typography5, { variant: "h3", className: contactBlockClasses.title }, title)),
      !!description && /* @__PURE__ */ React9.createElement(Box5, null, /* @__PURE__ */ React9.createElement(Typography5, { variant: "body1", className: contactBlockClasses.description }, description)),
      !!buttonLabel && /* @__PURE__ */ React9.createElement(Box5, null, /* @__PURE__ */ React9.createElement(
        Button2,
        {
          variant: "contained",
          color: "primary",
          component: LinkComponent || "a",
          ...buttonLink || {}
        },
        buttonLabel
      )),
      !!phoneNumber && /* @__PURE__ */ React9.createElement(
        Button2,
        {
          className: contactBlockClasses.button,
          variant: "text",
          startIcon: /* @__PURE__ */ React9.createElement(Call, null),
          component: "a",
          href: `tel:${phoneNumber}`
        },
        `${phoneText} ${phoneNumber}`
      )
    )
  )));
}

// src/Header/Header.tsx
import { Box as Box6, useMediaQuery, useTheme } from "@mui/material";
import React21 from "react";

// src/Header/HeaderDesktop.tsx
import { Container, styled as styled11 } from "@mui/material";
import React18 from "react";

// src/Header/MenuLink.tsx
import React11 from "react";

// src/Header/SubMenu.tsx
import { Menu, MenuItem as MuiMenuItem, styled as styled8 } from "@mui/material";
import React10 from "react";
import { ExpandMore as ExpandMore2, ExpandLess } from "@mui/icons-material";
var StyledA = styled8("a")(({ theme }) => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    alignContent: "center",
    padding: theme.spacing(2),
    color: "white",
    fontSize: "1.25rem"
  }
}));
var StyledMenuItem = styled8(MuiMenuItem)(({ theme }) => ({
  "& a": {
    width: "100%",
    padding: theme.spacing(0.5),
    "& span": {
      color: theme.palette.text.primary
    },
    "&:hover": {
      textDecoration: "none"
    },
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      display: "flex",
      alignContent: "center"
    }
  }
}));
function SubMenu({ item, toggleMenu, DefaultLinkComponent }) {
  const [anchorEl, setAnchorEl] = React10.useState(null);
  const open = !!anchorEl;
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleItemClose = () => {
    handleClose();
    if (toggleMenu) {
      toggleMenu();
    }
  };
  return /* @__PURE__ */ React10.createElement(React10.Fragment, null, /* @__PURE__ */ React10.createElement(StyledA, { onClick: handleClick, role: "menuitem" }, /* @__PURE__ */ React10.createElement("span", null, item.name), open ? /* @__PURE__ */ React10.createElement(ExpandLess, null) : /* @__PURE__ */ React10.createElement(ExpandMore2, null)), /* @__PURE__ */ React10.createElement(
    Menu,
    {
      anchorEl,
      open,
      onClose: handleClose,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: { vertical: "top", horizontal: "left" },
      disableScrollLock: true
    },
    item.children?.map((child, index) => /* @__PURE__ */ React10.createElement(StyledMenuItem, { key: index }, /* @__PURE__ */ React10.createElement(MenuLink_default, { item: child, toggleMenu: handleItemClose, DefaultLinkComponent })))
  ));
}
var SubMenu_default = SubMenu;

// src/Header/headerClasses.ts
var baseClassMobile = "HeaderMobile";
var baseClassDesktop = "HeaderDesktop";
var baseClassMobileOverlay = "HeaderMobileOverlay";
var headerMobileClasses = {
  root: `${baseClassMobile}-root`,
  navCenter: `${baseClassMobile}-navCenter`,
  navRight: `${baseClassMobile}-navRight`,
  logo: `${baseClassMobile}-logo`,
  iconMargin: `${baseClassMobile}-iconMargin`,
  profileTitle: `${baseClassMobile}-profileTitle`
};
var headerDesktopClasses = {
  root: `${baseClassDesktop}-root`,
  nav: `${baseClassDesktop}-nav`,
  left: `${baseClassDesktop}-left`,
  logo: `${baseClassDesktop}-logo`,
  links: `${baseClassDesktop}-links`,
  rightMenu: `${baseClassDesktop}-rightMenu`,
  iconMargin: `${baseClassDesktop}-iconMargin`,
  profileTitle: `${baseClassDesktop}-profileTitle`
};
var headerMobileOverlayClasses = {
  overlayLogo: `${baseClassMobileOverlay}-overlayLogo`,
  header: `${baseClassMobileOverlay}-header`,
  closeBtn: `${baseClassMobileOverlay}-closeBtn`,
  menuItems: `${baseClassMobileOverlay}-menuItems`,
  menuLink: `${baseClassMobileOverlay}-menuLink`,
  iconMargin: `${baseClassMobileOverlay}-iconMargin`,
  footer: `${baseClassMobileOverlay}-footer`,
  flag: `${baseClassMobileOverlay}-flag`,
  langBtn: `${baseClassMobileOverlay}-langBtn`,
  active: `${baseClassMobileOverlay}-active`
};

// src/Header/MenuLink.tsx
function MenuLink({
  item,
  toggleMenu,
  DefaultLinkComponent
}) {
  if (item.children && item.children.length > 0) return /* @__PURE__ */ React11.createElement(SubMenu_default, { item, toggleMenu, DefaultLinkComponent });
  const Component = item.LinkComponent || DefaultLinkComponent || "a";
  const handleOnClick = (event) => {
    if (toggleMenu) {
      toggleMenu();
    }
    if (Object.getOwnPropertyNames(item.linkAttributes).includes("onClick")) {
      item.linkAttributes.onClick(event);
    }
  };
  return /* @__PURE__ */ React11.createElement(Component, { ...item.linkAttributes || {}, className: headerMobileOverlayClasses.menuLink, onClick: handleOnClick }, item.name);
}
var MenuLink_default = MenuLink;

// src/Header/HeaderLocale.tsx
import { ListItemIcon, MenuItem } from "@mui/material";
import React17 from "react";

// src/MenuDropdown/menuDropdownClasses.ts
var baseClass6 = "MenuDropdown";
var classes6 = {
  toggleButton: `${baseClass6}-toggleButton`,
  menu: `${baseClass6}-menu`,
  expandIcon: `${baseClass6}-expandIcon`
};
var menuDropdownClasses = classes6;

// src/MenuDropdown/MenuDropdown.tsx
import React12, { useState as useState3, useEffect, useRef as useRef2 } from "react";
import { styled as styled9, Menu as Menu2, Button as Button3, buttonClasses } from "@mui/material";
import { ExpandLess as ExpandLess2, ExpandMore as ExpandMore3 } from "@mui/icons-material";
var StyledMenuButton = styled9(Button3)(({ theme }) => ({
  [`&.${menuDropdownClasses.toggleButton}`]: {
    color: theme.palette.common.black,
    fontSize: theme.typography.body1.fontSize,
    "&:focus, &:hover": {
      background: "transparent"
    }
  },
  [`& .${buttonClasses.endIcon}`]: {
    marginLeft: theme.spacing(0.5)
  }
}));
var StyledExpandIcon = styled9(
  ({ open, ...rest }) => open ? /* @__PURE__ */ React12.createElement(ExpandLess2, { ...rest }) : /* @__PURE__ */ React12.createElement(ExpandMore3, { ...rest })
)(({ theme }) => ({
  [`&.${menuDropdownClasses.expandIcon}`]: {
    fontSize: "1.25rem",
    color: theme.palette.icon.dropdown
  }
}));
function MenuDropdown({ children, id = `menu-dropdown-${Math.floor(Math.random() * 100)}`, menuLabel, icon, open = false, ...rest }) {
  const [isOpen, setIsOpen] = useState3(open);
  const menuRef = useRef2(null);
  const handleMenuClick = () => {
    setIsOpen((prevState) => !prevState);
  };
  const handleMenuClose = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    if (open && !isOpen) {
      setIsOpen(true);
    }
    if (!open && isOpen) {
      setIsOpen(false);
    }
  }, [open]);
  return /* @__PURE__ */ React12.createElement(React12.Fragment, null, /* @__PURE__ */ React12.createElement(
    StyledMenuButton,
    {
      className: menuDropdownClasses.toggleButton,
      id: `${id}-button`,
      "aria-controls": isOpen ? id : void 0,
      "aria-haspopup": "true",
      "aria-expanded": isOpen,
      size: "small",
      disableRipple: true,
      disableFocusRipple: true,
      onClick: handleMenuClick,
      ref: menuRef,
      startIcon: icon,
      endIcon: /* @__PURE__ */ React12.createElement(StyledExpandIcon, { open: isOpen, className: menuDropdownClasses.expandIcon }),
      ...rest
    },
    /* @__PURE__ */ React12.createElement("span", { style: { marginTop: "-0.15em" } }, menuLabel)
  ), /* @__PURE__ */ React12.createElement(
    Menu2,
    {
      id,
      className: menuDropdownClasses.menu,
      "aria-labelledby": `${id}-button`,
      anchorEl: menuRef.current,
      open: !!isOpen,
      onClose: handleMenuClose,
      onClick: handleMenuClose,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "right"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "right"
      }
    },
    children
  ));
}

// src/Header/HeaderFlag.tsx
import { styled as styled10 } from "@mui/material";
import React16 from "react";

// src/Header/images/FlagDe.tsx
import React13 from "react";
function FlagDe({ ...props }) {
  return /* @__PURE__ */ React13.createElement("svg", { width: "20px", height: "20px", viewBox: "0 0 20 20", version: "1.1", xmlns: "http://www.w3.org/2000/svg", ...props }, /* @__PURE__ */ React13.createElement("defs", null, /* @__PURE__ */ React13.createElement("circle", { id: "path-1", cx: "10", cy: "10", r: "10" })), /* @__PURE__ */ React13.createElement("g", { id: "FlagDE", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React13.createElement("g", { id: "Header_states", transform: "translate(-955, -134)" }, /* @__PURE__ */ React13.createElement("g", { id: "Group-2", transform: "translate(250, 40)" }, /* @__PURE__ */ React13.createElement("g", { id: "Dropdown-Copy", transform: "translate(693, 39)" }, /* @__PURE__ */ React13.createElement("g", { id: "Flag", transform: "translate(12, 55)" }, /* @__PURE__ */ React13.createElement("mask", { id: "mask-2", fill: "white" }, /* @__PURE__ */ React13.createElement("use", { xlinkHref: "#path-1" })), /* @__PURE__ */ React13.createElement("circle", { stroke: "#979797", cx: "10", cy: "10", r: "9.5" }), /* @__PURE__ */ React13.createElement("g", { id: "Flag_of_Germany", mask: "url(#mask-2)", fillRule: "nonzero" }, /* @__PURE__ */ React13.createElement("g", { transform: "translate(-6, 0)" }, /* @__PURE__ */ React13.createElement("rect", { id: "black_stripe", fill: "#000000", x: "0", y: "0", width: "33", height: "20" }), /* @__PURE__ */ React13.createElement("rect", { id: "red_stripe", fill: "#DD0000", x: "0", y: "6.66666667", width: "33", height: "13.3333333" }), /* @__PURE__ */ React13.createElement("rect", { id: "gold_stripe", fill: "#FFCE00", x: "0", y: "13.3333333", width: "33", height: "6.66666667" })))))))));
}
var FlagDe_default = FlagDe;

// src/Header/images/FlagEn.tsx
import React14 from "react";
function FlagDe2({ ...props }) {
  return /* @__PURE__ */ React14.createElement("svg", { width: "20px", height: "20px", viewBox: "0 0 20 20", version: "1.1", xmlns: "http://www.w3.org/2000/svg", ...props }, /* @__PURE__ */ React14.createElement("defs", null, /* @__PURE__ */ React14.createElement("circle", { id: "path-1", cx: "10", cy: "10", r: "10" }), /* @__PURE__ */ React14.createElement("polygon", { id: "path-3", points: "0 0 0 20 40 20 40 0" }), /* @__PURE__ */ React14.createElement("path", { d: "M20,10 L40,10 L40,20 L20,10 Z M20,10 L20,20 L0,20 L20,10 Z M20,10 L0,10 L0,0 L20,10 Z M20,10 L20,0 L40,0 L20,10 Z", id: "path-5" })), /* @__PURE__ */ React14.createElement("g", { id: "FlagEN", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React14.createElement("g", { id: "Header_states", transform: "translate(-955, -91)" }, /* @__PURE__ */ React14.createElement("g", { id: "Group-2", transform: "translate(250, 40)" }, /* @__PURE__ */ React14.createElement("g", { id: "Dropdown-Copy", transform: "translate(693, 39)" }, /* @__PURE__ */ React14.createElement("g", { id: "Item-1", transform: "translate(0, 12)" }, /* @__PURE__ */ React14.createElement("g", { id: "Flag", transform: "translate(12, 0)" }, /* @__PURE__ */ React14.createElement("mask", { id: "mask-2", fill: "white" }, /* @__PURE__ */ React14.createElement("use", { xlinkHref: "#path-1" })), /* @__PURE__ */ React14.createElement("circle", { stroke: "#979797", cx: "10", cy: "10", r: "9.5" }), /* @__PURE__ */ React14.createElement("g", { id: "Flag_of_the_United_Kingdom-(1)", mask: "url(#mask-2)" }, /* @__PURE__ */ React14.createElement("g", { transform: "translate(-10, 0)" }, /* @__PURE__ */ React14.createElement("mask", { id: "mask-4", fill: "white" }, /* @__PURE__ */ React14.createElement("use", { xlinkHref: "#path-3" })), /* @__PURE__ */ React14.createElement("g", { id: "Path", stroke: "none", fill: "none" }), /* @__PURE__ */ React14.createElement("g", { id: "Group", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd", mask: "url(#mask-4)" }, /* @__PURE__ */ React14.createElement("polygon", { id: "Path", fill: "#012169", fillRule: "nonzero", points: "0 0 0 20 40 20 40 0" }), /* @__PURE__ */ React14.createElement("path", { d: "M0,0 L40,20 M40,0 L0,20", id: "Shape", stroke: "#FFFFFF", strokeWidth: "4", fill: "#000000", fillRule: "nonzero" }), /* @__PURE__ */ React14.createElement("g", { id: "Clipped" }, /* @__PURE__ */ React14.createElement("mask", { id: "mask-6", fill: "white" }, /* @__PURE__ */ React14.createElement("use", { xlinkHref: "#path-5" })), /* @__PURE__ */ React14.createElement("g", { id: "Shape" }), /* @__PURE__ */ React14.createElement("path", { d: "M0,0 L40,20 M40,0 L0,20", id: "Shape", stroke: "#C8102E", strokeWidth: "2.66666667", fill: "#000000", fillRule: "nonzero", mask: "url(#mask-6)" })), /* @__PURE__ */ React14.createElement("path", { d: "M20,0 L20,20 M0,10 L40,10", id: "Shape", stroke: "#FFFFFF", strokeWidth: "6.66666667", fill: "#000000", fillRule: "nonzero" }), /* @__PURE__ */ React14.createElement("path", { d: "M20,0 L20,20 M0,10 L40,10", id: "Shape", stroke: "#C8102E", strokeWidth: "4", fill: "#000000", fillRule: "nonzero" })))))))))));
}
var FlagEn_default = FlagDe2;

// src/Header/images/FlagNl.tsx
import React15 from "react";
function FlagNl({ ...props }) {
  return /* @__PURE__ */ React15.createElement("svg", { width: "20px", height: "20px", viewBox: "0 0 20 20", version: "1.1", xmlns: "http://www.w3.org/2000/svg", ...props }, /* @__PURE__ */ React15.createElement("defs", null, /* @__PURE__ */ React15.createElement("circle", { id: "path-1", cx: "10", cy: "10", r: "10" })), /* @__PURE__ */ React15.createElement("g", { id: "FlagNL", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React15.createElement("g", { id: "Header_states", transform: "translate(-955, -44)" }, /* @__PURE__ */ React15.createElement("g", { id: "Group-2", transform: "translate(250, 40)" }, /* @__PURE__ */ React15.createElement("g", { id: "Flag", transform: "translate(705, 4)" }, /* @__PURE__ */ React15.createElement("mask", { id: "mask-2", fill: "white" }, /* @__PURE__ */ React15.createElement("use", { xlinkHref: "#path-1" })), /* @__PURE__ */ React15.createElement("circle", { stroke: "#979797", cx: "10", cy: "10", r: "9.5" }), /* @__PURE__ */ React15.createElement("image", { id: "Bitmap", mask: "url(#mask-2)", x: "-5.5", y: "0", width: "30", height: "20", xlinkHref: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAP8AAACqCAYAAABiS5mOAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAA/6ADAAQAAAABAAAAqgAAAABhyc50AAAEmElEQVR4Ae3WUQ3CQBREUUrWBQjAST9qpTawww9JBeCiakpTG/esgznzJtnp83wdN48AgZzAPZdYYAIELgHjdwgEogLGHy1ebALG7wYIRAWMP1q82ASM3w0QiAoYf7R4sQkYvxsgEBUw/mjxYhMwfjdAICpg/NHixSZg/G6AQFTA+KPFi03A+N0AgaiA8UeLF5uA8bsBAlEB448WLzYB43cDBKICxh8tXmwCxu8GCEQFjD9avNgEjN8NEIgKGH+0eLEJGL8bIBAVMP5o8WITMH43QCAqYPzR4sUmYPxugEBUwPijxYtNwPjdAIGogPFHixebgPG7AQJRAeOPFi82AeN3AwSiAsYfLV5sAsbvBghEBYw/WrzYBIzfDRCIChh/tHixCRi/GyAQFTD+aPFiEzB+N0AgKmD80eLFJmD8boBAVMD4o8WLTcD43QCBqIDxR4sXm4DxuwECUQHjjxYvNgHjdwMEogLGHy1ebALG7wYIRAWMP1q82ASM3w0QiAoYf7R4sQmMxzJTIEAgKDAd5wvmFplAXsC3P38CAKoCxl9tXu68gPHnTwBAVcD4q83LnRcw/vwJAKgKGH+1ebnzAsafPwEAVQHjrzYvd17A+PMnAKAqYPzV5uXOCxh//gQAVAWMv9q83HkB48+fAICqgPFXm5c7L2D8+RMAUBUw/mrzcucFjD9/AgCqAsZfbV7uvIDx508AQFXA+KvNy50XMP78CQCoChh/tXm58wLGnz8BAFUB4682L3dewPjzJwCgKmD81eblzgsYf/4EAFQFjL/avNx5AePPnwCAqoDxV5uXOy9g/PkTAFAVMP5q83LnBYw/fwIAqgLGX21e7ryA8edPAEBVwPirzcudFzD+/AkAqAoYf7V5ufMCxp8/AQBVAeOvNi93XsD48ycAoCpg/NXm5c4LGH/+BABUBYy/2rzceQHjz58AgKqA8VeblzsvYPz5EwBQFTD+avNy5wWMP38CAKoCxl9tXu68gPHnTwBAVcD4q83LnRcw/vwJAKgKGH+1ebnzAsafPwEAVYGxvr/V7HITSAuM7benAYQnUBXw7a82L3dewPjzJwCgKmD81eblzgsYf/4EAFQFjL/avNx5AePPnwCAqoDxV5uXOy9g/PkTAFAVMP5q83LnBYw/fwIAqgLGX21e7ryA8edPAEBVwPirzcudFzD+/AkAqAoYf7V5ufMCxp8/AQBVAeOvNi93XsD48ycAoCpg/NXm5c4LGH/+BABUBYy/2rzceQHjz58AgKqA8VeblzsvYPz5EwBQFTD+avNy5wWMP38CAKoCxl9tXu68gPHnTwBAVcD4q83LnRcw/vwJAKgKGH+1ebnzAsafPwEAVQHjrzYvd17A+PMnAKAqYPzV5uXOCxh//gQAVAWMv9q83HkB48+fAICqgPFXm5c7L2D8+RMAUBUw/mrzcucFjD9/AgCqAsZfbV7uvIDx508AQFXA+KvNy50XMP78CQCoChh/tXm58wLGnz8BAFUB4682L3dewPjzJwCgKmD81eblzgsYf/4EAFQFjL/avNx5AePPnwCAqoDxV5uXOy/wB7fpC+jpfyuaAAAAAElFTkSuQmCC" }))))));
}
var FlagNl_default = FlagNl;

// src/Header/HeaderFlag.tsx
function Flag({ locale, ...rest }) {
  switch (locale) {
    case "nl":
      return /* @__PURE__ */ React16.createElement(FlagNl_default, { ...rest });
    case "en":
      return /* @__PURE__ */ React16.createElement(FlagEn_default, { ...rest });
    case "de":
      return /* @__PURE__ */ React16.createElement(FlagDe_default, { ...rest });
    default:
      return /* @__PURE__ */ React16.createElement(FlagNl_default, { ...rest });
  }
}
var HeaderFlag = styled10(Flag)(() => ({
  display: "inline-block",
  height: "1rem",
  width: "1rem"
}));
var HeaderFlag_default = HeaderFlag;

// src/Header/HeaderLocale.tsx
function HeaderLocale({ currentLocale = { name: "Dutch", tag: "nl" }, localeOptions }) {
  const locale = currentLocale.tag.substring(0, 2);
  return /* @__PURE__ */ React17.createElement(
    MenuDropdown,
    {
      id: "header-locale",
      menuLabel: locale.toUpperCase(),
      icon: currentLocale.IconComponent ? /* @__PURE__ */ React17.createElement(currentLocale.IconComponent, null) : /* @__PURE__ */ React17.createElement(HeaderFlag_default, { locale })
    },
    localeOptions.map((option) => /* @__PURE__ */ React17.createElement(
      MenuItem,
      {
        component: option.LinkComponent || "a",
        disabled: option.locale.tag.substring(0, 2) === locale,
        key: option.locale.tag,
        ...option.linkAttributes || {}
      },
      /* @__PURE__ */ React17.createElement(ListItemIcon, null, option.locale.IconComponent ? /* @__PURE__ */ React17.createElement(option.locale.IconComponent, null) : /* @__PURE__ */ React17.createElement(HeaderFlag_default, { locale: option.locale.tag.substring(0, 2) })),
      option.locale.name.replace(/\(.*\)/, "")
    ))
  );
}
var HeaderLocale_default = HeaderLocale;

// src/Header/HeaderDesktop.tsx
var StyledContainer = styled11(Container)(({ theme }) => ({
  [`&.${headerDesktopClasses.root}`]: {},
  [`& .${headerDesktopClasses.nav}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: theme.palette.text.primary,
    "& a": {
      color: theme.palette.text.primary
    }
  },
  [`& .${headerDesktopClasses.left}`]: {
    display: "flex",
    alignItems: "center"
  },
  [`& .${headerDesktopClasses.logo}`]: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2, 2, 2, 0),
    padding: theme.spacing(1, 4, 1, 0),
    position: "relative",
    whiteSpace: "nowrap",
    fontWeight: 600,
    [theme.breakpoints.down("lg")]: {
      margin: theme.spacing(1, 1, 1, 0),
      padding: theme.spacing(1, 2, 1, 0)
    },
    "&:hover, &:focus": {
      color: theme.palette.common.black,
      textDecoration: "none"
    },
    "& img": {
      height: "2rem",
      marginTop: "0.25rem",
      marginBottom: "0.25rem",
      marginRight: theme.spacing(2)
    },
    "&:after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: "1rem",
      width: "1px",
      height: "calc(100% - 2em)",
      display: "block",
      background: "rgba(0,0,0,0.4)"
    }
  },
  [`& .${headerDesktopClasses.links}`]: {
    display: "inline-flex",
    flexWrap: "wrap",
    "& a": {
      padding: theme.spacing(2),
      "&:hover, &:focus": {
        textDecoration: "none",
        textShadow: `0 0 1px ${theme.palette.text.primary}`
      },
      [theme.breakpoints.down("lg")]: {
        padding: theme.spacing(0, 1)
      }
    }
  },
  [`& .${headerDesktopClasses.rightMenu}`]: {
    display: "flex",
    "& > button, & > a": {
      display: "inline-flex",
      alignItems: "center",
      lineHeight: "1rem",
      marginLeft: theme.spacing(0.5),
      padding: theme.spacing(1),
      background: "none",
      border: 0,
      whiteSpace: "nowrap",
      fontWeight: theme.typography.fontWeightRegular,
      "&:hover, &:focus": {
        textDecoration: "none",
        textShadow: `0 0 1px ${theme.palette.text.primary}`
      }
    }
  },
  [`& .${headerDesktopClasses.iconMargin}`]: {
    marginRight: theme.spacing(1)
  },
  [`& .${headerDesktopClasses.profileTitle}`]: {
    marginTop: theme.spacing(0.5)
  }
}));
var MenuGroup = styled11("div")(({ theme }) => ({
  "& a": {
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      display: "flex",
      alignContent: "center",
      padding: theme.spacing(2),
      color: "white",
      fontSize: "1.25rem"
    }
  }
}));
function HeaderDesktop({
  menu,
  currentLocale,
  localeOptions,
  siteName,
  desktopLogoUrl,
  LinkComponentHome,
  homeLinkAttributes,
  userProfileElement,
  DefaultLinkComponent
}) {
  return /* @__PURE__ */ React18.createElement(StyledContainer, { className: headerDesktopClasses.root }, /* @__PURE__ */ React18.createElement("nav", { "aria-label": "navigation", role: "navigation", className: headerDesktopClasses.nav, id: `navigation-${siteName}` }, /* @__PURE__ */ React18.createElement("div", { className: headerDesktopClasses.left, role: "menubar" }, /* @__PURE__ */ React18.createElement(LinkComponentHome, { className: headerDesktopClasses.logo, ...homeLinkAttributes || {} }, /* @__PURE__ */ React18.createElement("img", { src: desktopLogoUrl, alt: siteName }), " ", siteName), /* @__PURE__ */ React18.createElement(MenuGroup, { className: headerDesktopClasses.links }, menu && menu.map((item, index) => /* @__PURE__ */ React18.createElement(MenuLink_default, { key: index, item, DefaultLinkComponent })))), /* @__PURE__ */ React18.createElement("div", { className: headerDesktopClasses.rightMenu }, /* @__PURE__ */ React18.createElement(
    HeaderLocale_default,
    {
      currentLocale,
      localeOptions
    }
  ), userProfileElement)));
}
var HeaderDesktop_default = HeaderDesktop;

// src/Header/HeaderMobile.tsx
import { Button as Button5, styled as styled13 } from "@mui/material";
import { Menu as Menu3 } from "@mui/icons-material";
import React20, { useState as useState5 } from "react";

// src/Header/HeaderMobileOverlay.tsx
import { alertClasses, Button as Button4, darken, Dialog, paperClasses, styled as styled12 } from "@mui/material";
import { ChevronLeft, CloseOutlined } from "@mui/icons-material";
import React19, { useState as useState4 } from "react";
import clsx2 from "clsx";
var Root2 = styled12(Dialog)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [`& .${paperClasses.root}:not(.${alertClasses.standardSuccess})`]: {
    background: darken(theme.palette.primary.dark, 0.4)
  },
  "& .Mui-disabled": {
    color: theme.palette.grey[800]
  },
  [`& .${headerMobileOverlayClasses.overlayLogo}`]: {
    display: "flex",
    alignItems: "center",
    color: "white",
    fontSize: "1rem",
    "&:hover, &:focus": {
      textDecoration: "none",
      color: "white"
    },
    "& img": {
      height: "2rem",
      marginRight: theme.spacing(2)
    }
  },
  [`& .${headerMobileOverlayClasses.header}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    borderBottom: `1px solid ${darken(theme.palette.primary.main, 0.25)}`
  },
  [`& .${headerMobileOverlayClasses.closeBtn}`]: {
    display: "flex",
    background: "none",
    border: 0,
    fontSize: "1.75rem",
    color: "white"
  },
  [`& .${headerMobileOverlayClasses.menuItems}`]: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto"
  },
  [`& .${headerMobileOverlayClasses.menuLink}`]: {
    display: "flex",
    alignContent: "center",
    padding: theme.spacing(2),
    color: "white",
    fontSize: "1.25rem"
  },
  [`& .${headerMobileOverlayClasses.footer}`]: {
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${darken(theme.palette.primary.main, 0.25)}`,
    padding: theme.spacing(2),
    "& button": {
      color: "white",
      padding: theme.spacing(1)
    }
  },
  [`& .${headerMobileOverlayClasses.flag}`]: {
    display: "inline-block",
    height: "1.25rem",
    width: "1.25rem",
    marginRight: theme.spacing(2),
    backgroundSize: "contain"
  },
  [`& .${headerMobileOverlayClasses.langBtn}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: 0,
    opacity: 1,
    color: "white",
    fontSize: "1.125rem",
    padding: theme.spacing(2)
  },
  [`& .${headerMobileOverlayClasses.active}`]: {
    fontWeight: "bold"
  }
}));
function HeaderMobileOverlay({
  menu,
  toggleMenu,
  currentLocale,
  localeOptions,
  siteName,
  LinkComponentHome,
  homeLinkAttributes,
  mobileLogoUrl,
  DefaultLinkComponent,
  open
}) {
  const [languagePicker, setLanguagePicker] = useState4(false);
  const toggleLanguagePicker = () => {
    setLanguagePicker((prevLanguagePicker) => !prevLanguagePicker);
  };
  const renderMenuItems = () => {
    if (languagePicker) {
      return localeOptions.map((option) => /* @__PURE__ */ React19.createElement(
        Button4,
        {
          component: option.LinkComponent || "a",
          className: clsx2(headerMobileOverlayClasses.langBtn, {
            [headerMobileOverlayClasses.active]: option.locale.tag === currentLocale.tag
          }),
          key: option.locale.tag,
          disabled: option.locale.tag === currentLocale.tag,
          onClick: toggleLanguagePicker,
          ...option.linkAttributes || {}
        },
        /* @__PURE__ */ React19.createElement(
          HeaderFlag_default,
          {
            locale: option.locale.tag.split("-")[0],
            className: headerMobileOverlayClasses.flag
          }
        ),
        option.locale.name
      ));
    }
    return menu.map(
      (item, index) => /* @__PURE__ */ React19.createElement(
        MenuLink_default,
        {
          key: index,
          item,
          toggleMenu,
          DefaultLinkComponent
        }
      )
    );
  };
  return /* @__PURE__ */ React19.createElement(Root2, { fullScreen: true, keepMounted: true, open, disablePortal: true }, /* @__PURE__ */ React19.createElement("div", { className: headerMobileOverlayClasses.header }, languagePicker ? /* @__PURE__ */ React19.createElement("button", { onClick: toggleLanguagePicker, className: headerMobileOverlayClasses.closeBtn, type: "button" }, /* @__PURE__ */ React19.createElement(ChevronLeft, { fontSize: "inherit" })) : /* @__PURE__ */ React19.createElement(LinkComponentHome, { ...homeLinkAttributes || {}, className: headerMobileOverlayClasses.overlayLogo, onClick: toggleMenu }, /* @__PURE__ */ React19.createElement("img", { src: mobileLogoUrl, alt: siteName }), " ", siteName), /* @__PURE__ */ React19.createElement("button", { onClick: toggleMenu, className: headerMobileOverlayClasses.closeBtn, type: "button" }, /* @__PURE__ */ React19.createElement(CloseOutlined, { fontSize: "inherit" }))), /* @__PURE__ */ React19.createElement("nav", { className: headerMobileOverlayClasses.menuItems }, renderMenuItems()), /* @__PURE__ */ React19.createElement("div", { className: headerMobileOverlayClasses.footer }, /* @__PURE__ */ React19.createElement(
    "button",
    {
      className: headerMobileOverlayClasses.langBtn,
      onClick: toggleLanguagePicker,
      type: "button"
    },
    /* @__PURE__ */ React19.createElement(
      HeaderFlag_default,
      {
        locale: currentLocale.tag.split("-")[0],
        className: headerMobileOverlayClasses.flag
      }
    ),
    currentLocale.name
  )));
}
var HeaderMobileOverlay_default = HeaderMobileOverlay;

// src/Header/HeaderMobile.tsx
var Nav = styled13("nav")(({ theme }) => ({
  [`&.${headerMobileClasses.root}`]: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
    alignItems: "center"
  },
  "& button, & a": {
    display: "inline-flex",
    color: "black",
    fontSize: "1.5rem",
    padding: theme.spacing(1)
  },
  [`& .${headerMobileClasses.navCenter}`]: {
    textAlign: "center"
  },
  [`& .${headerMobileClasses.navRight}`]: {
    textAlign: "right",
    marginRight: theme.spacing(1),
    [`& .${headerMobileClasses.iconMargin}`]: {
      marginRight: theme.spacing(1)
    },
    [`& .${headerMobileClasses.profileTitle}`]: {
      marginTop: theme.spacing(0.5)
    }
  },
  [`& .${headerMobileClasses.logo}`]: {
    "& img": {
      height: "3rem",
      marginTop: "0.25rem"
    }
  }
}));
function HeaderMobile({
  menu,
  localeOptions,
  currentLocale,
  siteName,
  mobileLogoUrl,
  LinkComponentHome,
  homeLinkAttributes,
  DefaultLinkComponent,
  userProfileElement
}) {
  const [open, setOpen] = useState5(false);
  const toggleMenu = () => {
    setOpen((isOpen) => !isOpen);
  };
  return /* @__PURE__ */ React20.createElement(React20.Fragment, null, /* @__PURE__ */ React20.createElement(Nav, { className: headerMobileClasses.root }, /* @__PURE__ */ React20.createElement("div", null, /* @__PURE__ */ React20.createElement(Button5, { onClick: toggleMenu }, /* @__PURE__ */ React20.createElement(Menu3, { fontSize: "inherit" }))), /* @__PURE__ */ React20.createElement("div", { className: headerMobileClasses.navCenter }, /* @__PURE__ */ React20.createElement(LinkComponentHome, { className: headerMobileClasses.logo, ...homeLinkAttributes || {} }, /* @__PURE__ */ React20.createElement("img", { src: mobileLogoUrl, alt: siteName }))), /* @__PURE__ */ React20.createElement("div", { className: headerMobileClasses.navRight }, userProfileElement)), /* @__PURE__ */ React20.createElement(
    HeaderMobileOverlay_default,
    {
      toggleMenu,
      menu,
      localeOptions,
      currentLocale,
      siteName,
      LinkComponentHome,
      homeLinkAttributes,
      mobileLogoUrl,
      DefaultLinkComponent,
      open
    }
  ));
}
var HeaderMobile_default = HeaderMobile;

// src/Header/Header.tsx
function Header({
  siteName,
  currentLocale,
  localeOptions,
  translationItems,
  desktopLogoUrl,
  mobileLogoUrl,
  menu,
  LinkComponentHome,
  homeLinkAttributes,
  userProfileElement,
  DefaultLinkComponent,
  showMobile,
  ...rest
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return /* @__PURE__ */ React21.createElement(Box6, { component: "header", ...rest }, showMobile ?? isMobile ? /* @__PURE__ */ React21.createElement(
    HeaderMobile_default,
    {
      menu,
      siteName,
      mobileLogoUrl,
      localeOptions: localeOptions || translationItems,
      currentLocale,
      LinkComponentHome: LinkComponentHome || "a",
      homeLinkAttributes,
      userProfileElement,
      DefaultLinkComponent
    }
  ) : /* @__PURE__ */ React21.createElement(
    HeaderDesktop_default,
    {
      menu,
      siteName,
      desktopLogoUrl,
      localeOptions: localeOptions || translationItems,
      currentLocale,
      LinkComponentHome: LinkComponentHome || "a",
      homeLinkAttributes,
      userProfileElement,
      DefaultLinkComponent
    }
  ));
}
var Header_default = Header;

// src/Footer/Footer.tsx
import { Container as Container2, styled as styled14 } from "@mui/material";
import React23 from "react";

// src/Footer/FooterColumn.tsx
import { Typography as Typography6 } from "@mui/material";
import React22 from "react";

// src/Footer/footerClasses.ts
var baseClass7 = "Footer";
var classes7 = {
  wrapper: `${baseClass7}-wrapper`,
  item: `${baseClass7}-item`,
  column: `${baseClass7}-column`,
  root: `${baseClass7}-root`
};
var footerClasses = classes7;

// src/Footer/FooterColumn.tsx
function FooterItemLink({ item, DefaultLinkComponent }) {
  if (!item.link) {
    return /* @__PURE__ */ React22.createElement("span", null, item.label);
  }
  const LinkComponent = item.LinkComponent || DefaultLinkComponent || "a";
  return /* @__PURE__ */ React22.createElement(LinkComponent, { ...item.link }, item.icon && /* @__PURE__ */ React22.createElement("img", { ...item.icon, src: item.icon.src, alt: item.icon.alt }), /* @__PURE__ */ React22.createElement("span", null, item.label));
}
function FooterItemList({ items, DefaultLinkComponent }) {
  if (!items || !items.length) {
    return null;
  }
  return /* @__PURE__ */ React22.createElement("ul", null, items.map((item, i) => /* @__PURE__ */ React22.createElement("li", { key: `${item.label}-${i}`, className: footerClasses.item }, /* @__PURE__ */ React22.createElement(FooterItemLink, { item, DefaultLinkComponent }), /* @__PURE__ */ React22.createElement(FooterItemList, { items: item.items, DefaultLinkComponent }))));
}
function FooterColumn({ item, DefaultLinkComponent }) {
  return /* @__PURE__ */ React22.createElement("div", { className: footerClasses.column }, /* @__PURE__ */ React22.createElement(Typography6, { variant: "h6", color: "common.white" }, item.label), /* @__PURE__ */ React22.createElement(FooterItemList, { items: item.items, DefaultLinkComponent }));
}
var FooterColumn_default = FooterColumn;

// src/Footer/Footer.tsx
var Root3 = styled14("footer")(({ theme }) => ({
  backgroundColor: theme.palette.background.footer,
  padding: theme.spacing(3, 0),
  [`& .${footerClasses.wrapper}`]: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gridGap: theme.spacing(2),
    zIndex: 45
  },
  [`& .${footerClasses.column} ul`]: {
    padding: 0,
    margin: 0,
    listStyle: "none"
  },
  [`& .${footerClasses.item}`]: {
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.grey[300],
    listStyle: "none",
    "& img, & span": {
      verticalAlign: "middle"
    },
    "& img": {
      marginRight: theme.spacing(1),
      width: 16
    },
    "& ul": {
      paddingLeft: theme.spacing(2)
    },
    "& a": {
      color: "inherit",
      "&:hover > span": {
        textDecoration: "underline"
      },
      [theme.breakpoints.down("sm")]: {
        display: "inline-flex",
        minHeight: "48px",
        minWidth: "48px",
        alignItems: "center"
      }
    }
  }
}));
function Footer({ items, children, DefaultLinkComponent, ...rest }) {
  return /* @__PURE__ */ React23.createElement(Root3, { className: footerClasses.root, ...rest }, /* @__PURE__ */ React23.createElement(Container2, { className: footerClasses.wrapper }, children, items?.map((item, i) => /* @__PURE__ */ React23.createElement(FooterColumn_default, { item, key: `${item.label}-${i}`, DefaultLinkComponent }))));
}
var Footer_default = Footer;

// src/Kpi/Kpi.tsx
import { Box as Box7, Grid as Grid4, styled as styled16 } from "@mui/material";
import React25 from "react";

// src/Kpi/KpiItem.tsx
import { Grid as Grid3, styled as styled15 } from "@mui/material";
import React24, { useCallback as useCallback2, useState as useState7 } from "react";

// src/utils/useCounter.ts
import { useEffect as useEffect2, useState as useState6 } from "react";
var frames = 25;
var easeOutLN = (goal, frame) => goal / Math.log(goal + 1) * Math.log(frame / frames * goal + 1);
var useCounter = (kpi, isEnabled) => {
  const enabled = isEnabled && Number.isInteger(kpi);
  const numberKpi = Number.isInteger(kpi) ? Number(kpi) : kpi;
  const [frame, setFrame] = useState6(0);
  useEffect2(() => {
    if (!enabled || frame >= frames) return void 0;
    const timeout = setTimeout(() => {
      setFrame((prevFrame) => prevFrame + 1);
    }, frame / frames * (400 / frames) + 100);
    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [frame, enabled]);
  if (!enabled || typeof numberKpi === "string") return numberKpi;
  return Math.min(Math.ceil(easeOutLN(numberKpi, frame)), numberKpi);
};
var useCounter_default = useCounter;

// src/Kpi/kpiClasses.ts
var baseClass8 = "Kpi";
var classes8 = {
  item: `${baseClass8}-item`,
  itemValue: `${baseClass8}-itemValue`,
  itemDescription: `${baseClass8}-itemDescription`,
  imageWrapper: `${baseClass8}-imageWrapper`
};
var kpiClasses = classes8;

// src/Kpi/KpiItem.tsx
var KpiDiv = styled15("div")(({ theme }) => ({
  [`&.${kpiClasses.item}`]: {
    ...theme.typography.body1,
    fontSize: "1.25rem",
    textAlign: "center",
    padding: theme.spacing(2, 4)
  },
  [`& .${kpiClasses.itemValue}`]: {
    fontSize: "3rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  [`& .${kpiClasses.imageWrapper}`]: {
    height: "3.5rem",
    width: "3.5rem",
    borderRadius: "50%",
    background: theme.palette.background.icon,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    "& img": {
      width: "100%",
      height: "100%"
    }
  }
}));
function KpiItem({ item, animation, count, index, hideImage }) {
  const [enabled, setEnabled] = useState7(animation ? false : count);
  const countingKpi = useCounter_default(item.kpi, enabled);
  const handleAnimationChange = useCallback2((inView) => {
    if (inView && count) setEnabled(true);
  }, [count]);
  return /* @__PURE__ */ React24.createElement(Grid3, { item: true, lg: 4, md: 4, sm: 4, xs: 12 }, /* @__PURE__ */ React24.createElement(AnimationWrapper_default, { animation, delay: 200 * index + 200, onVisibilityChange: handleAnimationChange }, /* @__PURE__ */ React24.createElement(KpiDiv, { className: kpiClasses.item }, !hideImage && item.iconSrc && /* @__PURE__ */ React24.createElement("div", { className: kpiClasses.imageWrapper }, /* @__PURE__ */ React24.createElement(
    "img",
    {
      src: item.iconSrc,
      alt: item.iconAlt ?? "",
      ...item.iconProps
    }
  )), /* @__PURE__ */ React24.createElement("div", { className: kpiClasses.itemValue }, count ? countingKpi : item.kpi, item.postfix || ""), /* @__PURE__ */ React24.createElement("div", { className: kpiClasses.itemDescription }, item.description))));
}
var KpiItem_default = KpiItem;

// src/Kpi/Kpi.tsx
var Root4 = styled16(Box7)(({ theme }) => ({
  padding: theme.spacing(0, 2)
}));
function Kpi({ title, description, items, animation, count, hideImages, ...rest }) {
  return /* @__PURE__ */ React25.createElement(Root4, { ...rest }, /* @__PURE__ */ React25.createElement(Subtitle_default, { title, description, sx: { mb: 2 } }), /* @__PURE__ */ React25.createElement(Grid4, { container: true, spacing: 2, justifyContent: "center" }, items.map((item, i) => /* @__PURE__ */ React25.createElement(KpiItem_default, { item, animation, count, index: i, hideImage: hideImages, key: i }))));
}

// src/NoSsrAndDebounce/NoSsrAndDebounce.tsx
import { useState as useState8, useEffect as useEffect3 } from "react";
var getReadyState = () => typeof document !== "undefined" && document.readyState === "complete";
function NoSsrAndDebounce({ children }) {
  const [show, setShow] = useState8(getReadyState());
  useEffect3(() => {
    const fn = () => {
      setShow(getReadyState());
    };
    if (typeof document !== "undefined") {
      if (document.readyState === "complete") {
        fn();
      } else {
        document.addEventListener("readystatechange", fn);
      }
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("readystatechange", fn);
      }
    };
  }, []);
  if (typeof window === "undefined" || !show) return null;
  return children;
}
var NoSsrAndDebounce_default = NoSsrAndDebounce;

// src/Reviews/Reviews.tsx
import { Box as Box8, Grid as Grid6 } from "@mui/material";
import clsx3 from "clsx";
import React27 from "react";

// src/Reviews/reviewsClasses.ts
var baseClass9 = "Reviews";
var classes9 = {
  root: `${baseClass9}-root`,
  review: `${baseClass9}-review`,
  reviewStars: `${baseClass9}-reviewStars`,
  reviewTitle: `${baseClass9}-reviewTitle`,
  reviewDescription: `${baseClass9}-reviewDescription`,
  reviewAuthor: `${baseClass9}-reviewAuthor`,
  authorImageWrapper: `${baseClass9}-authorImageWrapper`,
  authorName: `${baseClass9}-authorName`
};
var reviewsClasses = classes9;

// src/Reviews/ReviewsItem.tsx
import React26 from "react";
import { Star, StarBorder, StarHalf } from "@mui/icons-material";
import { Grid as Grid5, styled as styled17 } from "@mui/material";
var starsIndexes = [1, 2, 3, 4, 5];
function getStarIcons(itemStars) {
  return starsIndexes.map((it) => {
    if (it <= itemStars) return Star;
    if (it > Math.ceil(itemStars)) return StarBorder;
    return StarHalf;
  });
}
var ReviewGrid = styled17(Grid5)(({ theme }) => ({
  [`& .${reviewsClasses.review}`]: {
    height: "100%",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  },
  [`& .${reviewsClasses.reviewStars}`]: {
    "& > *": {
      color: "#e8833a",
      fontSize: "1.4375rem"
    }
  },
  [`& .${reviewsClasses.reviewTitle}`]: {
    fontSize: "1rem",
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  [`& .${reviewsClasses.reviewDescription}`]: {
    fontSize: "1rem"
  },
  [`& .${reviewsClasses.reviewDescription}, .${reviewsClasses.authorName}`]: {
    ...theme.typography.body1
  },
  [`& .${reviewsClasses.reviewAuthor}`]: {
    ...theme.typography.body1,
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1)
  },
  [`& .${reviewsClasses.authorImageWrapper}`]: {
    height: "2.5rem",
    width: "2.5rem",
    borderRadius: "50%",
    background: `linear-gradient(180deg, #E3EAEF 0%, ${theme.palette.common.white} 100%)`,
    "& img": {
      height: "100%",
      width: "100%",
      borderRadius: "50%"
    }
  }
}));
function ReviewsItem({ item, animation, index, columnCount }) {
  return /* @__PURE__ */ React26.createElement(ReviewGrid, { item: true, md: 12 / columnCount, xs: 12, key: item.title }, /* @__PURE__ */ React26.createElement(
    AnimationWrapper_default,
    {
      animation,
      delay: 200 * index + 200,
      triggerOnce: true,
      style: {
        height: "100%"
      }
    },
    /* @__PURE__ */ React26.createElement("div", { className: reviewsClasses.review }, /* @__PURE__ */ React26.createElement("div", null, item.stars !== void 0 && /* @__PURE__ */ React26.createElement("div", { className: reviewsClasses.reviewStars }, getStarIcons(item.stars).map((StarIcon, i) => /* @__PURE__ */ React26.createElement(StarIcon, { key: i }))), !!item.title && /* @__PURE__ */ React26.createElement("h5", { className: reviewsClasses.reviewTitle }, item.title), /* @__PURE__ */ React26.createElement("div", { className: reviewsClasses.reviewDescription }, item.description)), item.authorName && /* @__PURE__ */ React26.createElement("div", { className: reviewsClasses.reviewAuthor }, /* @__PURE__ */ React26.createElement("span", { className: reviewsClasses.authorImageWrapper }, item.authorImage && /* @__PURE__ */ React26.createElement("img", { src: item.authorImage, alt: `Review by ${item.authorName}` })), /* @__PURE__ */ React26.createElement("div", { className: reviewsClasses.authorName }, item.authorName)))
  ));
}
var ReviewsItem_default = ReviewsItem;

// src/Reviews/Reviews.tsx
function Reviews({ items, title, description, animation, columnCount = 3, center = true, ...rest }) {
  return /* @__PURE__ */ React27.createElement(Box8, { ...rest, className: clsx3(reviewsClasses.root, rest.className) }, /* @__PURE__ */ React27.createElement(
    Subtitle_default,
    {
      title,
      description,
      sx: { mb: 4 }
    }
  ), /* @__PURE__ */ React27.createElement(Grid6, { container: true, spacing: 2, justifyContent: center ? "center" : "stretch", alignItems: "stretch" }, items.map((item, index) => /* @__PURE__ */ React27.createElement(ReviewsItem_default, { item, animation, index, columnCount, key: `${item.title}-${index}` }))));
}
var Reviews_default = Reviews;

// src/Title/Title.tsx
import { Grid as Grid7, useMediaQuery as useMediaQuery2, useTheme as useTheme2, styled as styled18 } from "@mui/material";
import React28 from "react";
var RootDiv4 = styled18("div")(({ theme }) => ({
  "& h1": {
    ...theme.typography.h1,
    fontSize: "2.5rem",
    // TODO: remove after theme from Check is imported
    fontWeight: theme.typography.fontWeightBold,
    textAlign: "center",
    color: theme.palette.text.primary,
    margin: 0
  }
}));
var DescriptionDiv = styled18("div")(({ theme }) => ({
  ...theme.typography.body1,
  fontSize: "1.125rem",
  marginTop: theme.spacing(2)
}));
function Title({ title, description, descriptionAlign, descriptionAlignMobile, moreWidth = false, ...rest }) {
  const theme = useTheme2();
  const isMobile = useMediaQuery2(theme.breakpoints.down("md"));
  const textAlignFinal = (isMobile ? descriptionAlignMobile || descriptionAlign : descriptionAlign || descriptionAlignMobile) || "justify";
  return /* @__PURE__ */ React28.createElement(RootDiv4, { ...rest }, /* @__PURE__ */ React28.createElement("h1", null, title), /* @__PURE__ */ React28.createElement(Grid7, { container: true, spacing: 2, justifyContent: "center" }, description && /* @__PURE__ */ React28.createElement(
    Grid7,
    {
      item: true,
      lg: moreWidth ? 8 : 6,
      md: moreWidth ? 9 : 7,
      sm: moreWidth ? 12 : 10,
      xs: 12
    },
    /* @__PURE__ */ React28.createElement(DescriptionDiv, { sx: { textAlign: textAlignFinal } }, description)
  )));
}
var Title_default = Title;

// src/Usp/Usp.tsx
import { Grid as Grid8, styled as styled20 } from "@mui/material";
import React30 from "react";

// src/Usp/UspItem.tsx
import { styled as styled19 } from "@mui/material";
import React29 from "react";

// src/Usp/uspClasses.ts
var baseClass10 = "Usp";
var classes10 = {
  items: `${baseClass10}-items`,
  imageWrapper: `${baseClass10}-imageWrapper`,
  itemIcon: `${baseClass10}-itemIcon`,
  itemTitle: `${baseClass10}-itemTitle`,
  itemDescription: `${baseClass10}-itemDescription`
};
var uspClasses = classes10;

// src/Usp/UspItem.tsx
var RootDiv5 = styled19("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  fontSize: "1rem",
  [`& .${uspClasses.imageWrapper}`]: {
    height: "2.8rem",
    width: "2.8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  [`& .${uspClasses.itemIcon}`]: {
    width: "100%",
    height: "100%"
  },
  [`& .${uspClasses.itemTitle}`]: {
    color: theme.palette.grey[900],
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2.4)
  },
  [`& .${uspClasses.itemDescription}`]: {
    color: theme.palette.grey[800],
    margin: 0,
    lineHeight: "1.5rem"
  }
}));
function UspItem({ title, description, imgSrc }) {
  return /* @__PURE__ */ React29.createElement(RootDiv5, null, /* @__PURE__ */ React29.createElement("div", { className: uspClasses.imageWrapper }, /* @__PURE__ */ React29.createElement("img", { className: uspClasses.itemIcon, src: imgSrc, alt: title })), /* @__PURE__ */ React29.createElement("strong", { className: uspClasses.itemTitle }, title), /* @__PURE__ */ React29.createElement("p", { className: uspClasses.itemDescription }, description));
}

// src/Usp/Usp.tsx
var RootDiv6 = styled20("div")({
  textAlign: "center",
  "& h1": {
    margin: 0,
    fontSize: "1.75rem",
    lineHeight: "2.5rem",
    fontWeight: "bold"
  },
  [`& .${uspClasses.items}`]: {
    justifyContent: "center"
  }
});
function Usp({ title, description, columnCount = 3, items, ...rest }) {
  return /* @__PURE__ */ React30.createElement(RootDiv6, { ...rest }, /* @__PURE__ */ React30.createElement(Subtitle_default, { title, description, sx: { mb: 4 } }), /* @__PURE__ */ React30.createElement(Grid8, { container: true, className: uspClasses.items, rowGap: 2 }, items.map((item) => /* @__PURE__ */ React30.createElement(Grid8, { item: true, xs: 12, sm: 12 / columnCount, key: `item_${item.title}` }, /* @__PURE__ */ React30.createElement(
    UspItem,
    {
      title: item.title,
      description: item.description,
      imgSrc: item.imgSrc || ""
    }
  )))));
}

// src/VideoWrapper/VideoWrapper.tsx
import { styled as styled21 } from "@mui/material";
import React31 from "react";

// src/VideoWrapper/videoWrapperClasses.ts
var baseClass11 = "VideoWrapper";
var classes11 = {
  playerWrapper: `${baseClass11}-playerWrapper`,
  player: `${baseClass11}-player`,
  playerFrame: `${baseClass11}-playerFrame`
};
var videoWrapperClasses = classes11;

// src/VideoWrapper/VideoWrapper.tsx
var RootDiv7 = styled21("div")({
  textAlign: "center",
  [`& .${videoWrapperClasses.playerWrapper}`]: {
    margin: "0 auto",
    maxWidth: "820px",
    aspectRatio: "16/9",
    [`& .${videoWrapperClasses.player}`]: {
      width: "100%",
      height: "100%"
    },
    [`& .${videoWrapperClasses.playerFrame}`]: {
      width: "100%",
      height: "100%",
      "& .ytp-chrome-top-buttons": {
        display: "none"
      }
    }
  }
});
var playersOptions = {
  playerVars: {
    modestbranding: 1
  }
};
function VideoWrapper({ title, description, renderVideo, ...rest }) {
  return /* @__PURE__ */ React31.createElement(RootDiv7, { ...rest }, /* @__PURE__ */ React31.createElement(Subtitle_default, { title, description, sx: { mb: 2 } }), /* @__PURE__ */ React31.createElement("div", { className: videoWrapperClasses.playerWrapper }, /* @__PURE__ */ React31.createElement(NoSsrAndDebounce_default, null, renderVideo({
    opts: playersOptions,
    className: videoWrapperClasses.player,
    iframeClassName: videoWrapperClasses.playerFrame
  }))));
}
var VideoWrapper_default = VideoWrapper;

// src/ClassicBanners/ClassicBanners.tsx
import React33 from "react";
import { Box as Box10, styled as styled22 } from "@mui/material";

// src/ClassicBanners/ClassicBanner.tsx
import React32 from "react";
import { Box as Box9, Typography as Typography7 } from "@mui/material";
import { KeyboardArrowRight } from "@mui/icons-material";

// src/ClassicBanners/classicBannersClasses.ts
var baseClass12 = "ClassicBanners";
var classes12 = {
  icon: `${baseClass12}-icon`,
  content: `${baseClass12}-content`,
  arrow: `${baseClass12}-arrow`,
  banner: `${baseClass12}-banner`,
  title: `${baseClass12}-title`,
  bannerWrapper: `${baseClass12}-bannerWrapper`
};
var classicBannersClasses = classes12;

// src/ClassicBanners/ClassicBanner.tsx
function ClassicBanner({ item, animation, index }) {
  const isLink = !!item.linkProps;
  const BannerLinkComponent = isLink ? item.LinkComponent || "a" : "div";
  return /* @__PURE__ */ React32.createElement(AnimationWrapper_default, { animation, delay: 200 * index + 200 }, /* @__PURE__ */ React32.createElement(BannerLinkComponent, { ...item.linkProps || {}, className: `${classicBannersClasses.banner} ${isLink ? "link" : ""}` }, item.iconSrc && /* @__PURE__ */ React32.createElement(
    "img",
    {
      src: item.iconSrc,
      alt: item.iconAlt,
      ...item.iconProps,
      className: classicBannersClasses.icon
    }
  ), /* @__PURE__ */ React32.createElement("div", { className: classicBannersClasses.content }, /* @__PURE__ */ React32.createElement(Box9, { sx: { flex: 1 } }, /* @__PURE__ */ React32.createElement(Typography7, { variant: "h3", className: classicBannersClasses.title, sx: { color: item.color } }, item.title), /* @__PURE__ */ React32.createElement(Typography7, null, item.description)), isLink && /* @__PURE__ */ React32.createElement(KeyboardArrowRight, { className: classicBannersClasses.arrow }))));
}
var ClassicBanner_default = ClassicBanner;

// src/ClassicBanners/ClassicBanners.tsx
var Root5 = styled22(Box10)(({ theme }) => ({
  [`& .${classicBannersClasses.bannerWrapper}`]: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
    gridGap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(1, 1fr)"
    }
  },
  [`& .${classicBannersClasses.banner}`]: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    background: theme.palette.grey[100],
    transition: "background ease-in 0.2s",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    "& p": {
      color: theme.palette.grey[600],
      fontSize: "1.03125rem",
      margin: 0,
      transition: "transform .2s ease-in .1s",
      [theme.breakpoints.down("md")]: {
        fontSize: "1.125rem",
        lineHeight: 1.4
      }
    },
    "&.link": {
      ":hover, :focus": {
        textDecoration: "none",
        background: theme.palette.grey[200],
        transition: "background ease-in 0.1s",
        "& h4, & p": {
          transform: "translateX(2px)"
        },
        [`& .${classicBannersClasses.icon}`]: {
          transform: "scale(1.1)"
        },
        [`& .${classicBannersClasses.arrow}`]: {
          transform: "scale(1.25) translateX(2px)"
        }
      }
    }
  },
  [`& .${classicBannersClasses.icon}`]: {
    height: "8.5rem",
    transition: "transform .2s ease-in",
    [theme.breakpoints.down("sm")]: {
      height: "10rem"
    }
  },
  [`& .${classicBannersClasses.content}`]: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    padding: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2, 0.5),
      marginLeft: theme.spacing(2)
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 0),
      marginLeft: theme.spacing(1)
    }
  },
  [`& .${classicBannersClasses.arrow}`]: {
    color: theme.palette.grey[500],
    fontSize: "2rem",
    margin: theme.spacing(0, 1, 0, 2),
    transition: "transform .2s ease-in .3s",
    [theme.breakpoints.down("md")]: {
      margin: theme.spacing(0, 0, 0, 0.5)
    },
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(0)
    }
  },
  [`& .${classicBannersClasses.title}`]: {
    fontSize: "1.3rem",
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(0, 0, 1, 0),
    transition: "transform .2s ease-in .1s",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(0.5)
    }
  }
}));
function ClassicBanners({ title, description, items, animation, DefaultLinkComponent, ...rest }) {
  return /* @__PURE__ */ React33.createElement(Root5, { ...rest }, /* @__PURE__ */ React33.createElement(Subtitle_default, { title, description, sx: { mb: 2 } }), /* @__PURE__ */ React33.createElement("div", { className: classicBannersClasses.bannerWrapper }, items.map((item, index) => /* @__PURE__ */ React33.createElement(
    ClassicBanner_default,
    {
      item: { ...item, LinkComponent: item.LinkComponent || DefaultLinkComponent },
      index,
      key: index,
      animation
    }
  ))));
}
var ClassicBanners_default = ClassicBanners;

// src/Card/Card.tsx
import React34 from "react";
import { lighten as lighten2, styled as styled23 } from "@mui/material";

// src/Card/cardClasses.ts
var baseClass13 = "Card";
var classes13 = {
  root: `${baseClass13}-root`,
  headerIcon: `${baseClass13}-iconHeader`,
  header: `${baseClass13}-header`,
  content: `${baseClass13}-content`
};
var cardClasses = classes13;

// src/Card/Card.tsx
var Root6 = styled23("div")(({ theme }) => ({
  backgroundColor: lighten2(theme.palette.grey[200], 0.2),
  borderRadius: theme.shape.borderRadius,
  height: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4)
  }
}));
function Card({ children, ...rest }) {
  return /* @__PURE__ */ React34.createElement(Root6, { className: cardClasses.root, ...rest }, children);
}

// src/Card/CardHeader.tsx
import React35 from "react";
import { styled as styled24, Typography as Typography8 } from "@mui/material";
var RootDiv8 = styled24("div")(() => ({
  [`& .${cardClasses.headerIcon}`]: {
    display: "block",
    "& > img": {
      height: 32,
      width: 32
    }
  }
}));
function CardHeader({ children, icon, title, subtitle, ...rest }) {
  return /* @__PURE__ */ React35.createElement(RootDiv8, { className: cardClasses.header, ...rest }, icon && /* @__PURE__ */ React35.createElement("div", { className: cardClasses.headerIcon }, /* @__PURE__ */ React35.createElement("img", { src: icon.url, alt: icon.alt })), title && /* @__PURE__ */ React35.createElement(Typography8, { variant: "h3" }, title), subtitle && /* @__PURE__ */ React35.createElement(Typography8, { fontWeight: "bold", component: "h4" }, subtitle), children);
}

// src/Card/CardContent.tsx
import React36 from "react";
import { Divider, styled as styled25 } from "@mui/material";
import clsx4 from "clsx";
var RootDiv9 = styled25("div")(({ theme }) => ({
  "&& ul": {
    margin: theme.spacing(0.5, 0)
  },
  "&& li": {
    textAlign: "left",
    paddingLeft: theme.spacing(0.25),
    margin: 0
  }
}));
function CardContent({ className, children, showDivider = false, ...rest }) {
  const content = /* @__PURE__ */ React36.createElement(RootDiv9, { className: clsx4([cardClasses.content, className]), ...rest }, children);
  if (showDivider) {
    return /* @__PURE__ */ React36.createElement(React36.Fragment, null, /* @__PURE__ */ React36.createElement(Divider, { sx: { my: 2 } }), content);
  }
  return content;
}

// src/AttributeCards/AttributeCards.tsx
import React37 from "react";
import { alpha as alpha3, Box as Box11, Grid as Grid9, styled as styled26, Typography as Typography9 } from "@mui/material";
import clsx5 from "clsx";

// src/AttributeCards/attributeCardsClasses.ts
var baseClass14 = "AttributeCards";
var classes14 = {
  highlight: `${baseClass14}-highlight`,
  highlightText: `${baseClass14}-highlightText`,
  header: `${baseClass14}-header`,
  attributes: `${baseClass14}-attributes`,
  featuredTitle: `${baseClass14}-featuredTitle`,
  title: `${baseClass14}-title`,
  attributeValue: `${baseClass14}-attributeValue`,
  attributeIcon: `${baseClass14}-attributeIcon`,
  card: `${baseClass14}-card`,
  highlightTextWrapper: `${baseClass14}-highlightTextWrapper`
};
var attributeCardsClasses = classes14;

// src/AttributeCards/AttributeCards.tsx
var RootDiv10 = styled26("div")(({ theme }) => ({
  [`& .${attributeCardsClasses.featuredTitle}`]: {
    color: theme.palette.border.purple,
    fontSize: "3rem",
    lineHeight: "3rem",
    margin: theme.spacing(0, 1.25, 0, 0),
    display: "inline-block",
    fontWeight: 400
  },
  [`& .${attributeCardsClasses.title}`]: {
    color: theme.palette.border.purple,
    margin: "0 auto 0 0",
    fontSize: "1.125rem",
    lineHeight: "1.25rem",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    fontWeight: 600,
    "& > p": {
      margin: 0,
      display: "inline"
    }
  },
  [`& .${attributeCardsClasses.attributes}`]: {
    gap: theme.spacing(2),
    display: "flex",
    flexDirection: "column"
  },
  [`& .${attributeCardsClasses.highlight}`]: {
    backgroundImage: theme.gradients.combined
  },
  [`& .${attributeCardsClasses.header}`]: {
    display: "flex",
    flex: 1,
    color: theme.palette.border.purple,
    flexWrap: "wrap"
  },
  [`& .${attributeCardsClasses.highlightTextWrapper}`]: {
    margin: "auto 0",
    padding: theme.spacing(0.5, 0),
    display: "flex"
  },
  [`& .${attributeCardsClasses.highlightText}`]: {
    padding: theme.spacing(0, 0.75),
    backgroundColor: alpha3(theme.palette.border.purple, 0.16),
    borderRadius: theme.shape.borderRadius / 4,
    color: theme.palette.primary.main,
    fontSize: "0.875rem",
    fontWeight: theme.typography.fontWeightRegular
  },
  [`& .${attributeCardsClasses.attributeValue}`]: {
    margin: 0,
    marginLeft: `calc(${theme.spacing(1)} + 1rem)`,
    color: theme.palette.grey[800]
  },
  [`& .${attributeCardsClasses.attributeIcon}`]: {
    width: "1rem",
    height: "1rem",
    marginRight: theme.spacing(1)
  }
}));
function AttributeCards({ items, ...rest }) {
  return /* @__PURE__ */ React37.createElement(RootDiv10, { ...rest }, /* @__PURE__ */ React37.createElement(Grid9, { container: true }, items?.map((item, i) => /* @__PURE__ */ React37.createElement(Grid9, { item: true, xs: 12, md: 4, key: i }, /* @__PURE__ */ React37.createElement(
    Card,
    {
      className: clsx5(attributeCardsClasses.card, {
        [attributeCardsClasses.highlight]: !!item.highlightText
      })
    },
    /* @__PURE__ */ React37.createElement(CardHeader, { className: attributeCardsClasses.header }, item.featuredTitle && /* @__PURE__ */ React37.createElement("span", { className: attributeCardsClasses.featuredTitle }, item?.featuredTitle), item.title && /* @__PURE__ */ React37.createElement("span", { className: attributeCardsClasses.title }, item.title), item.highlightText && /* @__PURE__ */ React37.createElement("div", { className: attributeCardsClasses.highlightTextWrapper }, /* @__PURE__ */ React37.createElement("span", { className: attributeCardsClasses.highlightText }, item.highlightText))),
    /* @__PURE__ */ React37.createElement(CardContent, { className: attributeCardsClasses.attributes, showDivider: true }, item.attributes?.map((attribute, attributeIndex) => /* @__PURE__ */ React37.createElement("div", { key: attributeIndex }, /* @__PURE__ */ React37.createElement(Box11, { display: "flex", alignItems: "center" }, attribute.icon && /* @__PURE__ */ React37.createElement(
      "img",
      {
        src: attribute.icon.url,
        alt: attribute.icon.alt,
        className: attributeCardsClasses.attributeIcon
      }
    ), /* @__PURE__ */ React37.createElement(Typography9, { display: "inline-block", color: "text.primary", fontWeight: "bold" }, attribute.name)), /* @__PURE__ */ React37.createElement("div", { className: attributeCardsClasses.attributeValue }, attribute.value))))
  )))));
}

// src/Underline/Underline.tsx
import { styled as styled27 } from "@mui/material";
var indent = "0.17em";
function getDirection(direction) {
  if (direction === "left") {
    return {
      paddingLeft: indent,
      marginLeft: `-${indent}`
    };
  }
  return {
    paddingRight: indent,
    "+ span": {
      marginLeft: `-${indent}`
    }
  };
}
var Underline = styled27("em")(({ direction, theme }) => ({
  fontStyle: "normal",
  position: "relative",
  zIndex: 1,
  // eslint-disable-next-line max-len
  background: `repeating-linear-gradient(to top, ${theme.palette.shadow.orange}, ${theme.palette.shadow.orange} 0.5em, transparent 0.5em, transparent )`,
  ...getDirection(direction),
  backgroundPositionX: direction === "left" ? `-${indent}` : indent,
  backgroundRepeat: "no-repeat",
  display: "inline"
}));
var Underline_default = Underline;

// src/styled/checkmarkListStyled/checkmarkListStyled.tsx
import { styled as styled28 } from "@mui/material";
var checkmarkListStyled = (component) => styled28(component)(({ theme }) => ({
  "& ul": {
    listStyle: "none",
    padding: theme.spacing(0, 0, 0, 4),
    "& li": {
      margin: theme.spacing(1, 0, 0),
      position: "relative",
      "& p": {
        margin: 0
      }
    },
    "& li:before": {
      position: "absolute",
      top: "50%",
      left: 0,
      transform: "translate(-150%, -50%)",
      content: '""',
      backgroundColor: theme.palette.primary.main,
      mask: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' focusable='false' aria-hidden='true' viewBox='0 0 24 24' data-testid='CheckIcon' id='svg'%3E%3Cpath d='M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'%3E%3C/path%3E%3C/svg%3E") no-repeat 50% 50%`,
      width: "1.5rem",
      height: "1.5rem"
    }
  }
}));
var checkmarkListStyled_default = checkmarkListStyled;
export {
  Accordion_default as Accordion,
  AttributeCards,
  Card,
  CardContent,
  CardHeader,
  Carousel,
  CarouselVariant,
  CircleBackgroundBanners,
  ClassicBanners_default as ClassicBanners,
  ContactBlock,
  Footer_default as Footer,
  Header_default as Header,
  HeaderFlag_default as HeaderFlag,
  Kpi,
  MenuDropdown,
  NoSsrAndDebounce_default as NoSsrAndDebounce,
  Reviews_default as Reviews,
  TextImage_default as TextImage,
  Title_default as Title,
  Underline_default as Underline,
  Usp,
  VideoWrapper_default as VideoWrapper,
  attributeCardsClasses,
  cardClasses,
  carouselClasses,
  checkmarkListStyled_default as checkmarkListStyled,
  circleBackgroundBannersClasses,
  classicBannersClasses,
  contactBlockClasses,
  footerClasses,
  headerDesktopClasses,
  headerMobileClasses,
  headerMobileOverlayClasses,
  kpiClasses,
  menuDropdownClasses,
  reviewsClasses,
  subtitleClasses,
  textImageClasses,
  uspClasses,
  videoWrapperClasses
};
//# sourceMappingURL=index.js.map